import React, { useState, useEffect } from "react";

import classnames from "classnames";
import { get, capitalize } from "lodash";
import formatCurrency from "Utils/formatCurrency";

import BadgeV2 from "Cobalt/BadgeV2";
import { LabelSmall, BodyLarge } from "Cobalt/BodyText";
import Button from "Cobalt/Button";
import { CarrierLogo } from "Cobalt/CarrierLogo";
import { Collapse } from "Cobalt/Collapse";
import { Popover } from "Cobalt/Popover";
import { Row } from "Common/Grid";
import { Div } from "Common/HTML";
import Icon from "Common/Icon";
import SIOverlayTrigger from "Common/SIOverlayTrigger";
import { BodyText } from "Theme/BodyText";

import type { MedicalPlanPrice, MedicalPlanRecommendation } from "Utils/types";

import makeBem from "Utils/makeBem";

const bem = makeBem("CobaltPlanCard");
export type RecommendationReasonProps = MedicalPlanRecommendation & {
  icon: string;
  muted?: boolean;
  className?: string;
};
export const RecommendationReason = ({ icon = "child", label, className, ...props }: RecommendationReasonProps) => (
  <BodyText className={classnames(bem("RecommendationReason"), className)} {...props}>
    <Icon icon={icon} className={bem("RecommendationReason", "dynamic-icon")} />
    {label}
  </BodyText>
);
export type RecommendedForProps = {
  plan: MedicalPlanPrice;
  isExpandable: boolean;
  className?: string;
};
export const RecommendedFor = ({ plan, isExpandable, className, ...props }: RecommendedForProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  // Collapse automagically (animated) if switching to compare mode
  useEffect(() => {
    if (!isExpandable && isExpanded) setIsExpanded(false);
  }, [isExpandable]);
  const REASON_LIMIT = 2;
  const initialReasons = plan.recommendation.slice(0, REASON_LIMIT);
  const moreReasons = plan.recommendation.slice(REASON_LIMIT);
  const hasMoreReasons = plan.recommendation.length > REASON_LIMIT;
  const moreReasonsTooltip = (
    <Popover showBackdrop={false} id="more-reasons">
      <Popover.Body>
        {moreReasons.map((reason, i) => (
          <RecommendationReason key={i} {...reason} />
        ))}
      </Popover.Body>
    </Popover>
  );
  return (
    <Div className={className} {...props}>
      <Div className={bem("RecommendedFor")}>
        {isExpandable && (
          <>
            {!isExpanded &&
              initialReasons.map((reason, i) => (
                <BadgeV2 size="md" color="secondary" key={i}>
                  <Icon icon={reason.icon} />
                  {reason.label}
                </BadgeV2>
              ))}
            {!isExpanded && hasMoreReasons && (
              <BodyText className={bem("MoreReasons")}>
                <SIOverlayTrigger placement="top" overlay={moreReasonsTooltip}>
                  {/* a wrapper element is required for the tooltip trigger */}
                  <span>{`& ${moreReasons.length} more`}</span>
                </SIOverlayTrigger>
              </BodyText>
            )}
          </>
        )}
      </Div>
      <Collapse in={isExpanded} m={0}>
        <Row>
          <Div className={bem("RecommendedFor")}>
            {plan.recommendation.map((reason, i) => (
              <RecommendationReason muted key={i} {...reason} />
            ))}
          </Div>
        </Row>
      </Collapse>
    </Div>
  );
};
export type FeatureBoxProps = {
  plan: MedicalPlanPrice;
  title: string;
  field?: keyof MedicalPlanPrice | string;
  format?: "currency" | "acronym" | "capitalize" | "logo" | "link" | "html";
  value?: string;
  className?: string;
};
export const FeatureBox = ({ plan, title, field, format, value, className, ...props }: FeatureBoxProps) => {
  let formattedField = typeof value !== "undefined" ? value : get(plan, field, "N/A");

  if (value) {
    formattedField = value;
  }

  switch (format) {
    case "currency":
      formattedField = formatCurrency(formattedField);
      break;

    case "acronym":
      formattedField = formattedField.toUpperCase();
      break;

    case "capitalize":
      formattedField = capitalize(formattedField);
      break;

    case "logo":
      formattedField = <CarrierLogo plan={plan} />;
      break;

    case "link":
      formattedField = field;
      break;

    default:
  }

  return (
    <Div className={classnames(bem("FeatureBox"), className)} {...props}>
      <LabelSmall mb={2} className="uppercase">
        {title}
      </LabelSmall>
      {format === "html" ? (
        <BodyLarge>
          <span dangerouslySetInnerHTML={{ __html: plan[field] }} />
        </BodyLarge>
      ) : (
        <BodyLarge>{formattedField}</BodyLarge>
      )}
    </Div>
  );
};
export const PlanHeader = ({ plan, ...props }: { plan: MedicalPlanPrice }) => (
  <Div className={bem("PlanHeader")} {...props}>
    <CarrierLogo plan={plan} />
  </Div>
);
export const MonthlyCost = ({
  cost,
  loading,
  hasFootnote,
  className = "text-right",
  costType = "average",
  ...props
}: {
  cost: number;
  loading: boolean;
  hasFootnote?: boolean;
  className?: string;
  costType?: "average" | "total";
}) => (
  <Div className={className} {...props}>
    {loading ? (
      <Icon icon="spinner" spin="pulse" size="3x" ml="auto" />
    ) : costType === "total" ? (
      <Div>
        <LabelSmall muted>{`Total Monthly Cost${hasFootnote ? "*" : ""}`}</LabelSmall>
        <BodyLarge weight="bold">{formatCurrency(cost, 2)}</BodyLarge>
      </Div>
    ) : (
      <Div>
        <BodyLarge weight="bold">{`${formatCurrency(cost)}/mo`}</BodyLarge>
        <LabelSmall>{`per employee${hasFootnote ? "*" : ""}`}</LabelSmall>
      </Div>
    )}
  </Div>
);
export const CollapseToggle = ({ isExpanded, toggle, ...props }: { isExpanded: boolean; toggle: () => void }) => (
  <Button color="ghost" circle size="xs" onClick={toggle}>
    <span className="sr-only">Click to {isExpanded ? "collapse" : "expand"} plan details.</span>
    <Icon name="toggle" size="2x" flavor="light" {...props} icon={isExpanded ? "angle-up" : "angle-down"} />
  </Button>
);
